/**
 * JSON-LD yardımcıları (BreadcrumbList, Organization, WebSite, Restaurant, WebPage, Article)
 */
import { absoluteImageUrl } from './seo';
import { schemaInLanguage } from './intl-seo';

const CTX = 'https://schema.org';

function omitEmpty(obj) {
  if (obj === null || obj === undefined) return undefined;
  if (Array.isArray(obj)) {
    const a = obj.map(omitEmpty).filter((x) => x !== undefined);
    return a.length ? a : undefined;
  }
  if (typeof obj !== 'object') return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    const val = omitEmpty(v);
    if (val === undefined) continue;
    if (typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length === 0) continue;
    out[k] = val;
  }
  return Object.keys(out).length ? out : undefined;
}

export function breadcrumbListJsonLd(items) {
  if (!items?.length) return null;
  return {
    '@context': CTX,
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: it.item,
    })),
  };
}

/** Anasayfa: Organization + WebSite (@graph) — searchTarget: intl-seo searchActionTargetUrl() ile (en kök /search) */
export function homeOrganizationAndWebsiteJsonLd({ site, langCode, description, searchTarget }) {
  if (!site) return null;
  const orgId = `${site}#organization`;
  const webId = `${site}#website`;
  const target =
    searchTarget ||
    (langCode === 'en'
      ? `${site}/search?q={search_term_string}`
      : `${site}/${langCode}/search?q={search_term_string}`);
  return {
    '@context': CTX,
    '@graph': omitEmpty([
      {
        '@type': 'Organization',
        '@id': orgId,
        name: 'Turkish E-Market',
        url: site,
        logo: {
          '@type': 'ImageObject',
          url: `${site}/logo.png`,
        },
      },
      {
        '@type': 'WebSite',
        '@id': webId,
        name: 'Turkish E-Market',
        url: site,
        description,
        publisher: { '@id': orgId },
        potentialAction: {
          '@type': 'SearchAction',
          target,
          'query-input': 'required name=search_term_string',
        },
      },
    ]),
  };
}

export function organizationJsonLd({ site, name = 'Turkish E-Market', contactPoints }) {
  if (!site) return null;
  const base = {
    '@context': CTX,
    '@type': 'Organization',
    name,
    url: site,
    logo: `${site}/logo.png`,
  };
  if (contactPoints?.length) {
    base.contactPoint = contactPoints.length === 1 ? contactPoints[0] : contactPoints;
  }
  return omitEmpty(base);
}

export function contactPointsFromApiContacts(contacts) {
  if (!contacts?.length) return [];
  return contacts
    .filter((c) => c.is_active !== false)
    .map((c) =>
      omitEmpty({
        '@type': 'ContactPoint',
        contactType: 'customer service',
        telephone: c.phone ? String(c.phone).replace(/\s/g, '') : undefined,
        email: c.email || undefined,
        areaServed: c.country_code ? String(c.country_code).toUpperCase() : undefined,
      }),
    )
    .filter(Boolean);
}

export function webPageJsonLd({ url, name, description, site }) {
  if (!url) return null;
  return omitEmpty({
    '@context': CTX,
    '@type': 'WebPage',
    name,
    description,
    url,
    isPartOf: site ? { '@type': 'WebSite', url: site } : undefined,
    publisher: site
      ? {
          '@type': 'Organization',
          name: 'Turkish E-Market',
          url: site,
          logo: `${site}/logo.png`,
        }
      : undefined,
  });
}

export function articleJsonLd({ url, headline, description, site, langCode }) {
  if (!url || !headline) return null;
  const logo = site ? `${site}/logo.png` : undefined;
  return omitEmpty({
    '@context': CTX,
    '@type': 'Article',
    headline,
    description,
    url,
    inLanguage: schemaInLanguage(langCode || 'en'),
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    author: {
      '@type': 'Organization',
      name: 'Turkish E-Market',
      url: site,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Turkish E-Market',
      url: site,
      logo: logo ? { '@type': 'ImageObject', url: logo } : undefined,
    },
  });
}

/** Restoran detay: Restaurant (LocalBusiness alt tipi) */
export function restaurantJsonLd({ restaurant, pageUrl, siteBase }) {
  if (!restaurant?.name || !pageUrl) return null;
  const img = restaurant.images?.[0]
    ? absoluteImageUrl(siteBase, restaurant.images[0])
    : undefined;
  const schema = {
    '@context': CTX,
    '@type': 'Restaurant',
    name: restaurant.name,
    url: pageUrl,
    image: img,
    address: restaurant.address
      ? omitEmpty({
          '@type': 'PostalAddress',
          streetAddress: restaurant.address,
          addressLocality: restaurant.city || undefined,
          addressCountry: restaurant.country_code || restaurant.country || undefined,
        })
      : undefined,
    telephone: restaurant.phone || undefined,
    priceRange: restaurant.price_range || undefined,
    servesCuisine: restaurant.cuisine_type || undefined,
  };
  if (restaurant.rating != null && restaurant.rating !== '') {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: Number(restaurant.rating),
      reviewCount: Math.max(0, Number(restaurant.review_count) || 0),
    };
  }
  return omitEmpty(schema);
}

/**
 * Product JSON-LD içindeki tekil Review öğeleri (Google rich results).
 * @param {Array} reviews — API /reviews/{productId}
 * @param {number} max — en fazla kaç yorum (Google genelde 5–10 önerir)
 */
export function productReviewSchemaItems(reviews, max = 10) {
  if (!reviews?.length) return undefined;

  const items = reviews
    .filter((r) => r && String(r.comment || '').trim())
    .slice(0, max)
    .map((r) => {
      const rating = Math.min(5, Math.max(1, Number(r.rating) || 0));
      const authorName = String(r.user_name || '').trim() || 'Müşteri';
      let datePublished;
      if (r.created_at) {
        try {
          datePublished = new Date(r.created_at).toISOString().split('T')[0];
        } catch {
          datePublished = undefined;
        }
      }
      return omitEmpty({
        '@type': 'Review',
        author: { '@type': 'Person', name: authorName },
        datePublished,
        reviewBody: String(r.comment).trim(),
        name: r.title?.trim() || undefined,
        reviewRating: {
          '@type': 'Rating',
          ratingValue: rating,
          bestRating: 5,
          worstRating: 1,
        },
      });
    })
    .filter(Boolean);

  return items.length ? items : undefined;
}

export function jsonLdScript(obj) {
  if (!obj) return '';
  return JSON.stringify(obj);
}
